// procedureIcons — Maltese cross + altitude-constraint glyphs for
// instrument procedure waypoints on the map.
//
// Renders three image atlases:
//   maltese-cross  : the FAF symbol (4 triangular points)
//   iaf-circle     : the IAF symbol (open circle with crosshairs)
//   map-square     : the MAP symbol (filled square)
//
// All registered with the map's image atlas via ensureSvgImages so a
// 'symbol' layer can reference them with 'icon-image': '<id>'.

import type { Map as MlMap } from 'maplibre-gl';
import { ensureSvgImages, type SvgSymbol } from './iconRegistry';

const MAGENTA = '#e040fb';
const STROKE  = '#0b0d0f';

// ── Maltese cross ────────────────────────────────────────────────────────
//
// The FAA standard Maltese cross has four pointed arms meeting at the
// center. Drawn at 32x32 px so it sits comfortably next to the magenta
// dot without overwhelming the route line.

const MALTESE_CROSS_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
  <g stroke="${STROKE}" stroke-width="1.5" stroke-linejoin="round" fill="${MAGENTA}">
    <!-- top -->
    <path d="M 16 2 L 19 11 L 16 14 L 13 11 Z"/>
    <!-- right -->
    <path d="M 30 16 L 21 19 L 18 16 L 21 13 Z"/>
    <!-- bottom -->
    <path d="M 16 30 L 13 21 L 16 18 L 19 21 Z"/>
    <!-- left -->
    <path d="M 2 16 L 11 13 L 14 16 L 11 19 Z"/>
  </g>
  <circle cx="16" cy="16" r="1.4" fill="${STROKE}" />
</svg>`.trim();

// ── IAF (Initial Approach Fix) ───────────────────────────────────────────
//
// Conventionally drawn as a small open square or open circle on FAA
// plates. We use an open circle with a magenta outline + center dot.

const IAF_CIRCLE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
  <circle cx="16" cy="16" r="10" fill="none" stroke="${MAGENTA}" stroke-width="2.5" />
  <circle cx="16" cy="16" r="1.6" fill="${MAGENTA}" stroke="${STROKE}" stroke-width="0.5" />
</svg>`.trim();

// ── MAP (Missed Approach Point) ──────────────────────────────────────────
//
// Plates often show the MAP as a filled square / runway end. We use a
// filled magenta square.

const MAP_SQUARE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
  <rect x="10" y="10" width="12" height="12" fill="${MAGENTA}" stroke="${STROKE}" stroke-width="1.5" />
</svg>`.trim();

// ── Symbol registration ─────────────────────────────────────────────────

const SYMBOLS: SvgSymbol[] = [
  { id: 'proc-maltese-cross', svg: MALTESE_CROSS_SVG },
  { id: 'proc-iaf-circle',    svg: IAF_CIRCLE_SVG },
  { id: 'proc-map-square',    svg: MAP_SQUARE_SVG },
];

export async function registerProcedureIcons(map: MlMap): Promise<void> {
  await ensureSvgImages(map, SYMBOLS, { defaultSize: 32, logTag: '[procedureIcons]' });
}

// ── Altitude constraint label formatting ─────────────────────────────────
//
// FAA plate convention:
//   line ABOVE altitude  = at or above (minimum)
//   line BELOW altitude  = at or below (maximum)
//   lines BOTH           = between (mandatory block)
//   no line              = exact altitude (mandatory)
//   parens               = recommended only
//
// MapLibre's text-field doesn't support inline graphics, so we encode
// the bar with Unicode combining overline + low line, but for clarity
// and font-portability we use a top-line / bottom-line glyph trick:
//   '+5000'        \u2192  '\u203E\n5000'              (overline + value on next line)
//   '-3000'        \u2192  '3000\n_____'           (value + underscore bar below)
//   'B5000/3000'   \u2192  '\u203E5000\n_3000'         (both)
//   'at5000'       \u2192  '5000'                  (no decoration)
//   '*5000'        \u2192  '(5000)'                (recommended)
//
// Even simpler approach used here: emit ASCII with arrow indicators
// so it's unambiguous in any font:
//   '+'  \u2192 'AT/ABV nnnn'  (read as horizontal bar above nnnn)
//   '-'  \u2192 'AT/BLW nnnn'
//   'B'  \u2192 'nnnn / nnnn'
//   'at' \u2192 'AT nnnn'
//   '*'  \u2192 '(nnnn)'
// Final approach: use the proper Unicode top-line / underscore
// glyphs which render fine in the configured Noto Sans font. We
// post-process the value to strip leading zeros and add the 'thousand'
// comma separator.

/** Strip the leading constraint char + zero-pad from a raw altitude
 *  string like '+ 04500', '00520', '* 02900', '- 03000'. Returns
 *  the numeric portion as a comma-formatted integer string, or null. */
export function formatAltValue(raw: string | null): string | null {
  if (!raw) return null;
  const num = raw.replace(/^[+\-*B]\s*/, '').replace(/^0+/, '').trim();
  if (!num) return null;
  if (/^FL\d+$/i.test(num)) {
    return num.toUpperCase();
  }
  const n = Number(num);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('en-US');
}

/** Build the multi-line text-field label for a fix's altitude
 *  constraint, using Unicode block characters for the bars. The
 *  bars render reliably in Noto Sans (Fyrefly's map font).
 *
 *  Returns null when there is no constraint to display.
 *
 *  FAA plate convention (NOT what you'd intuit — verified against
 *  AIM 5-4-5 and the standard d-TPP minima legend):
 *    line BELOW value  = at or above ('+')   minimum altitude floor
 *    line ABOVE value  = at or below ('-')   maximum altitude ceiling
 *    BOTH lines        = between ('B')        mandatory altitude block
 *    no line           = at exactly ('at')    mandatory exact altitude
 *    parens            = recommended ('*')    advisory only
 *
 *  Output is a multi-line string suitable for MapLibre 'text-field':
 *    '+'           → '5,000\n▁▁▁▁▁'   (floor: line below = 'fly at-or-ABOVE this')
 *    '-'           → '▔▔▔▔▔\n3,000'   (ceiling: line above = 'fly at-or-BELOW this')
 *    'B'           → '▔▔▔▔▔\n5,000\n3,000\n▁▁▁▁▁'   (block: 3000–5000)
 *    'at'          → '5,000'
 *    'recommended' → '(5,000)'
 */
export function formatAltitudeLabel(
  constraint: '+' | '-' | 'B' | 'at' | 'recommended' | null,
  altitude1: string | null,
  altitude2: string | null,
): string | null {
  const v1 = formatAltValue(altitude1);
  if (!v1) return null;
  // Bar width matches the digit count exactly so the line doesn't
  // extend far past the number. Block chars are roughly 0.5em wide
  // so we use the same character count as the value text.
  const barLen = v1.length;
  const upper = '▔'.repeat(barLen);
  const lower = '▁'.repeat(barLen);
  switch (constraint) {
    // At-or-above (floor): bar UNDER the number.
    case '+':           return `${v1}\n${lower}`;
    // At-or-below (ceiling): bar OVER the number.
    case '-':           return `${upper}\n${v1}`;
    case 'B': {
      // Between (mandatory block): bar above HIGH, bar below LOW.
      // CIFP convention: altitude1 = high (ceiling), altitude2 = low (floor).
      const v2 = formatAltValue(altitude2);
      if (!v2) return v1;
      return `${upper}\n${v1}\n${v2}\n${lower}`;
    }
    case 'at':          return v1;
    case 'recommended': return `(${v1})`;
    default:            return v1;
  }
}
